
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChecklistWithTaches } from '@/hooks/useNouvellesChecklists';
import { Plus, X } from 'lucide-react';

interface FormulaireModificationChecklistProps {
  checklist: ChecklistWithTaches;
  onModifierChecklist: (id: string, nom: string, taches: string[]) => Promise<void>;
  onAnnuler: () => void;
}

const FormulaireModificationChecklist = ({ 
  checklist, 
  onModifierChecklist, 
  onAnnuler 
}: FormulaireModificationChecklistProps) => {
  const [nom, setNom] = useState(checklist.nom);
  const [taches, setTaches] = useState<string[]>(
    checklist.taches.map(t => t.tache)
  );
  const [loading, setLoading] = useState(false);

  const ajouterTache = () => {
    setTaches([...taches, '']);
  };

  const supprimerTache = (index: number) => {
    setTaches(taches.filter((_, i) => i !== index));
  };

  const modifierTache = (index: number, valeur: string) => {
    const nouvellesTaches = [...taches];
    nouvellesTaches[index] = valeur;
    setTaches(nouvellesTaches);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!nom.trim()) {
      alert('Veuillez saisir un nom pour la checklist');
      return;
    }

    const tachesNonVides = taches.filter(t => t.trim() !== '');
    if (tachesNonVides.length === 0) {
      alert('Veuillez ajouter au moins une tâche');
      return;
    }

    setLoading(true);
    try {
      await onModifierChecklist(checklist.id, nom.trim(), tachesNonVides);
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert('Erreur lors de la modification de la checklist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="nom">Nom de la checklist</Label>
        <Input
          id="nom"
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="Nom de la checklist"
          required
        />
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Label>Tâches</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={ajouterTache}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter une tâche
          </Button>
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {taches.map((tache, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={tache}
                onChange={(e) => modifierTache(index, e.target.value)}
                placeholder={`Tâche ${index + 1}`}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => supprimerTache(index)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>

        {taches.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            Aucune tâche. Cliquez sur "Ajouter une tâche" pour commencer.
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={onAnnuler}
          disabled={loading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="bg-nautic hover:bg-nautic-light text-white"
        >
          {loading ? 'Modification...' : 'Modifier la checklist'}
        </Button>
      </div>
    </form>
  );
};

export default FormulaireModificationChecklist;
